import { t } from 'i18next';
import { Link, To } from 'react-router-dom';
import { DatevEntryTypesMapping } from './Datev.helpers';
import NewDatevItem, { DatevTileItem } from './NewDatevItem';
import OverviewIcon from '@/icons/assesment.svg?react';
import Button from '../Button';
import { postGenerateDatev } from '@/core/api';
import RegenerateIcon from '@/icons/regenerate.svg?react';
import { DatevFile } from '@/core/types';
import { ReportResultStatusEnum } from '@/core/enums';
import { useAppSelector, usePrevious } from '@/hooks';
import { useEffect, useMemo, useState } from 'react';
import useNotification from '@/hooks/useNotification';
import { normalizeDateFE } from '@/core/normalization';
import MiniLoader from '../MiniLoader/MiniLoader';
import NoDataIcon from '@/icons/no-data.svg?react';
import DownloadCloudIcon from '@/icons/download-cloud.svg?react';
import { formatDate } from '@/core/helpers';
import AlertIcon from '@/icons/alert-orange.svg?react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeySelector } from '@/core/store/user/userSlice';

export interface IDatevTile {
  items: DatevTileItem[];
  month: Date;
  counts: Record<number, number>;
  files: DatevFile[];
}

interface Props {
  tile: IDatevTile;
  to: To;
  companyId: string;
  isGeneratingAllowed: boolean;
}

export const DatevTile = ({
  tile,
  to,
  companyId,
  isGeneratingAllowed,
}: Props) => {
  const queryClient = useQueryClient();
  const queryKey = useAppSelector(queryKeySelector);
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotification();
  const isGeneratingFiles = tile.files.some(
    (f) =>
      f.status !== ReportResultStatusEnum.STATUS_READY &&
      f.status !== ReportResultStatusEnum.STATUS_ERROR
  );

  const files = tile.files;
  const prevFiles = usePrevious(files);
  useEffect(() => {
    if (
      (prevFiles || []).some(
        (f) =>
          f.status === ReportResultStatusEnum.STATUS_REQUESTED ||
          f.status === ReportResultStatusEnum.STATUS_STARTED
      ) &&
      files.every(
        (f) =>
          f.status === ReportResultStatusEnum.STATUS_READY ||
          f.status === ReportResultStatusEnum.STATUS_ERROR
      )
    ) {
      showNotification({
        text: `Datev files are generated for ${formatDate(
          normalizeDateFE(files[0].service_date),
          'MMMM yyyy'
        )}`,
        type: 'success',
      });
    }
    // eslint-disable-next-line
  }, [files]);

  const lastGeneratedDate = useMemo(() => {
    let latest: Date | undefined = undefined;
    files.forEach((file) => {
      const date = new Date(file.created_at);
      if (!latest || date > latest) {
        latest = date;
      }
    });
    return latest;
  }, [files]);

  const onClickGenerate = () => {
    if (!companyId || !isGeneratingAllowed) return;
    setIsLoading(true);
    postGenerateDatev(companyId, {
      year: tile.month.getFullYear(),
      month: tile.month.getMonth() + 1,
    })
      .then((res) => {
        queryClient.setQueryData(
          ['SellerDatev', queryKey],
          (prev: DatevFile[]) => {
            return prev
              .filter((f) => !tile.files.some((tf) => tf.id === f.id))
              .concat(res);
          }
        );
      })
      .catch(console.error)
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (!tile.items.length) {
    return (
      <div className='rounded-lg border'>
        <div className='border-b'>
          <div className='px-6 py-2 font-extrabold'>
            {formatDate(tile.month, 'MMMM yyyy')}
          </div>
        </div>
        <div className='flex items-center justify-between px-6 py-4'>
          <div className='flex items-center gap-x-2'>
            {isGeneratingAllowed ? (
              <>
                <div className='flex h-10 w-10 items-center justify-center rounded-full bg-gray-100'>
                  <NoDataIcon />
                </div>
                <span className='font-semibold'>
                  {t('Your data is ready for generating the DATEV exports')}
                </span>
              </>
            ) : (
              <>
                <AlertIcon />
                <span className='font-semibold'>
                  {t('Submit and confirm data to generate DATEV exports')}
                </span>
              </>
            )}
          </div>
          <Button
            className='flex items-center gap-x-2'
            onClick={onClickGenerate}
            disabled={isLoading || isGeneratingFiles || !isGeneratingAllowed}
          >
            <DownloadCloudIcon />
            <span>{t('Generate')}</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='rounded-lg border'>
      <div className='border-b px-6 py-2'>
        <div className='font-extrabold'>
          {formatDate(tile.month, 'MMMM yyyy')}
        </div>
        <div className='flex items-center justify-between gap-x-2'>
          {lastGeneratedDate && (
            <div className='text-gray-700'>
              {t('Last generated')}:{' '}
              {formatDate(lastGeneratedDate, 'dd.MM.yyyy HH:mm')}
            </div>
          )}
          <div>
            {!isLoading && !isGeneratingFiles && (
              <Button
                disabled={
                  isLoading || isGeneratingFiles || !isGeneratingAllowed
                }
                className='group flex items-center gap-x-1 !font-semibold'
                styling='naked'
                onClick={onClickGenerate}
              >
                <RegenerateIcon className='[&>g>path]:transition-[fill] group-hover:[&>g>path]:fill-green' />
                <span>{t('Regenerate')}</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {isLoading || isGeneratingFiles ? (
        <div className='flex items-center gap-x-2 px-6 py-4'>
          <MiniLoader />
          <span>{t('Generating files')}</span>
        </div>
      ) : (
        <>
          <div className='divide-y'>
            {!isGeneratingAllowed && (
              <div className='px-6 py-2'>
                <div className='flex gap-x-8 rounded-lg bg-orange-200 p-3'>
                  <div className='text-orange-500'>
                    {t(
                      'Your data has been unlocked. Data in newly generated exports might be no longer consistent with the data that has been filed. Please contact your CSM if needed.'
                    )}
                  </div>
                </div>
              </div>
            )}
            {tile.items.map((f) => {
              return (
                <NewDatevItem
                  item={f}
                  key={
                    f.option +
                    f.asciiFileId +
                    f.extfFileId +
                    tile.month.toISOString()
                  }
                  companyId={companyId}
                  count={tile.counts[DatevEntryTypesMapping[f.option]]}
                  period={tile.month}
                />
              );
            })}
          </div>
          <div className='border-t py-2'>
            {!!Object.keys(tile.counts).length && (
              <Link
                className='Button--naked group flex w-full items-center justify-center gap-x-2 !font-semibold'
                to={to}
              >
                <OverviewIcon className='[&>path]:transition-[fill] group-hover:[&>path]:fill-green' />
                <span>{t('Transactions overview')}</span>
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  );
};
